// Hook custom per recuperare contenuti di policy/privacy da Iubenda.
// Nota: uso axios e TypeScript con gestione sicura degli errori senza 'any'.

import { useState, useEffect } from "react";
import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_IUB_PRIVACY_POLICY_URL;

// Tipizzazione del risultato dell'hook
interface PolicyResponse {
    policyHTML: string | null;
    loading: boolean;
    error: string | null;
}

/**
 * useGetPolicyUrls
 * @param policyId - ID della policy fornito da Iubenda
 * @param privacyPolicy - true per privacy, false per cookie policy
 *
 * Nota per il prossimo dev:
 * - `policyHTML` contiene il contenuto HTML della policy
 * - `loading` true durante la chiamata API
 * - `error` messaggio di errore in caso di fallimento
 */
export const useGetPolicyUrls = (policyId: number, privacyPolicy: boolean): PolicyResponse => {

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [policyHTML, setPolicyHTML] = useState<string | null>(null);

    useEffect(() => {
        // Validazione ID
        if (!policyId || typeof policyId !== 'number' || policyId <= 0) {
            setLoading(false);
            setError("ID Policy non valido fornito.");
            return;
        }

        const fetchPolicyDetails = async () => {
            setLoading(true);
            setError(null);
            setPolicyHTML(null);

            const requestUrl = privacyPolicy
                ? `${API_BASE_URL}${policyId}`
                : `${API_BASE_URL}${policyId}/cookie-policy`;

            try {
                const { data } = await axios.get(requestUrl);

                if (data && typeof data === 'object' && 'content' in data) {
                    setPolicyHTML(data.content);
                } else {
                    throw new Error("Risposta da Iubenda non contiene il campo 'content' atteso.");
                }
            } catch (err: unknown) {
                let message = "Errore durante il recupero della policy da Iubenda.";
                if (axios.isAxiosError(err)) {
                    message = err.response?.data?.error || err.message || message;
                } else if (err instanceof Error) {
                    message = err.message;
                }
                console.error("Errore useGetPolicyUrls:", message);
                setError(message);
            } finally {
                setLoading(false);
            }
        };

        fetchPolicyDetails();
    }, [policyId, privacyPolicy]);

    return { loading, error, policyHTML };
};